<template>
  <div class="prod-stack prod-stack--wide">
    <ProductionSectionHead title="Analytics" subtitle="Manual snapshots of how the release is performing." />

    <section class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div v-for="metric in metrics" :key="metric.key" class="move-stat-card move-stat-card--secondary !min-h-0">
        <p class="move-stat-card__label">{{ metric.label }}</p>
        <p class="move-stat-card__value !text-2xl">{{ formatNumber(summary.totals?.[metric.key] || 0) }}</p>
      </div>
    </section>

    <form class="move-card prod-panel grid gap-4 sm:grid-cols-2" @submit.prevent="add">
      <h3 class="sm:col-span-2 text-base font-bold move-text">Add snapshot</h3>
      <div class="move-field !mb-0">
        <label for="snap-date">Date</label>
        <input id="snap-date" v-model="form.captured_on" type="date" required class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="snap-platform">Platform</label>
        <MoveSelect id="snap-platform" v-model="form.platform" block :options="platformOptions" />
      </div>
      <div class="move-field !mb-0">
        <label for="snap-views">Views</label>
        <input id="snap-views" v-model.number="form.views" type="number" min="0" class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="snap-likes">Likes</label>
        <input id="snap-likes" v-model.number="form.likes" type="number" min="0" class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="snap-comments">Comments</label>
        <input id="snap-comments" v-model.number="form.comments" type="number" min="0" class="move-input" />
      </div>
      <div class="move-field !mb-0">
        <label for="snap-shares">Shares</label>
        <input id="snap-shares" v-model.number="form.shares" type="number" min="0" class="move-input" />
      </div>
      <div class="move-field !mb-0 sm:col-span-2">
        <label for="snap-url">Published link</label>
        <input id="snap-url" v-model="form.url" type="text" inputmode="url" autocomplete="url" class="move-input" placeholder="https://youtube.com/watch?v=…" />
      </div>
      <div class="move-field !mb-0 sm:col-span-2">
        <label for="snap-notes">Notes</label>
        <input id="snap-notes" v-model="form.notes" class="move-input" />
      </div>
      <div class="sm:col-span-2">
        <button type="submit" class="move-btn-primary">Save snapshot</button>
      </div>
    </form>

    <div class="move-card overflow-hidden !p-0">
      <div class="overflow-x-auto">
        <table class="prod-table min-w-full">
          <thead>
            <tr>
              <th>Date</th>
              <th>Platform</th>
              <th>Link</th>
              <th>Views</th>
              <th>Likes</th>
              <th>Comments</th>
              <th>Shares</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in summary.history || []" :key="row.id">
              <td>{{ row.captured_on }}</td>
              <td class="move-text-muted">{{ platformLabels[row.platform] || row.platform }}</td>
              <td>
                <a
                  v-if="row.url"
                  :href="row.url"
                  target="_blank"
                  rel="noopener"
                  class="prod-link-chip !px-2.5 !py-1"
                >
                  Open
                  <span class="material-icons">open_in_new</span>
                </a>
                <span v-else class="move-text-faint">—</span>
              </td>
              <td>{{ formatNumber(row.views) }}</td>
              <td>{{ formatNumber(row.likes) }}</td>
              <td>{{ formatNumber(row.comments) }}</td>
              <td>{{ formatNumber(row.shares) }}</td>
              <td class="text-right">
                <button type="button" class="move-icon-btn move-icon-btn--danger" aria-label="Delete snapshot" @click="askRemove(row)">
                  <span class="material-icons">close</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!(summary.history || []).length" class="px-5 py-10 text-center text-sm move-text-faint">
        No campaign snapshots yet. Add numbers as the release performs.
      </p>
    </div>

    <MoveConfirmDialog
      v-model="showRemoveModal"
      title="Delete snapshot"
      subtitle="This action cannot be undone."
      :detail-title="snapshotTitle"
      :detail-body="snapshotToRemove?.notes || ''"
      detail-tone="danger"
      confirm-label="Delete snapshot"
      confirm-icon="delete"
      busy-label="Deleting…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemove"
      @cancel="closeRemove"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'
import { platformLabels, platformOptions } from '../../config/productionNavigation'

const workspace = inject('productionWorkspace')
const summary = ref({ totals: {}, history: [] })
const form = reactive({
  captured_on: new Date().toISOString().slice(0, 10),
  platform: 'youtube',
  url: '',
  views: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  notes: ''
})

const metrics = [
  { key: 'views', label: 'Views' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' }
]

function formatNumber (value) {
  return Number(value || 0).toLocaleString()
}

watch(() => form.platform, (platform) => {
  form.url = workspace.production.value?.release_links?.[platform] || ''
})

watch(() => workspace.production.value?.release_links, (links) => {
  if (!form.url) {
    form.url = links?.[form.platform] || ''
  }
}, { immediate: true })

async function load () {
  const body = await productionApi.analytics(workspace.productionId.value)
  summary.value = body.data || { totals: {}, history: [] }
}

async function add () {
  await productionApi.createSnapshot(workspace.productionId.value, { ...form })
  form.notes = ''
  await load()
  await workspace.reload()
  form.url = workspace.production.value?.release_links?.[form.platform] || form.url
}

const showRemoveModal = ref(false)
const snapshotToRemove = ref(null)
const removing = ref(false)
const removeError = ref('')
const snapshotTitle = computed(() => {
  const row = snapshotToRemove.value
  if (!row) return ''
  const platform = platformLabels[row.platform] || row.platform || 'Snapshot'
  return row.captured_on ? `${platform} · ${row.captured_on}` : platform
})

function askRemove (row) {
  snapshotToRemove.value = row
  removeError.value = ''
  showRemoveModal.value = true
}

function closeRemove () {
  if (removing.value) return
  showRemoveModal.value = false
  snapshotToRemove.value = null
  removeError.value = ''
}

async function confirmRemove () {
  const row = snapshotToRemove.value
  if (!row || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.deleteSnapshot(workspace.productionId.value, row.id)
    showRemoveModal.value = false
    snapshotToRemove.value = null
    await load()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not delete this snapshot.'
  } finally {
    removing.value = false
  }
}

onMounted(load)
</script>
